import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';

import { fToNow } from 'src/utils/format-time';

import { Iconify } from 'src/components/iconify';

import { färgFörDomän } from './icons';

import type { Notifiering } from 'src/actions/notifieringar';

// ----------------------------------------------------------------------

export type NotificationItemProps = {
  notifiering: Notifiering;
  /** Den förening som är vald i menyn — raderna som hör dit behöver ingen föreningsetikett. */
  valdOrgId?: string;
  onKlick: (notifiering: Notifiering) => void;
  onArkivera: (id: string) => void;
};

export function NotificationItem({
  notifiering,
  valdOrgId,
  onKlick,
  onArkivera,
}: NotificationItemProps) {
  const färg = färgFörDomän(notifiering.domän);

  const renderIkon = () => (
    <ListItemAvatar>
      <Box
        sx={{
          width: 40,
          height: 40,
          display: 'flex',
          borderRadius: '50%',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: `${färg}.lighter`,
          color: `${färg}.dark`,
        }}
      >
        {/* Ikonnamnet kommer från backends NotifieringsTyp — en ny typ får rätt ikon utan
            någon ändring här. */}
        <Iconify width={22} icon={notifiering.ikon} />
      </Box>
    </ListItemAvatar>
  );

  const renderText = () => (
    <ListItemText
      disableTypography
      primary={
        // Titel och beskrivning renderas som text, aldrig som HTML: innehållet kommer delvis
        // från användare (brevlådemeddelanden, utskickskommentarer).
        <Box sx={{ typography: 'subtitle2', mb: 0.5 }}>{notifiering.titel}</Box>
      }
      secondary={
        <>
          {notifiering.beskrivning && (
            <Box
              sx={{
                mb: 0.5,
                typography: 'body2',
                color: 'text.secondary',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {notifiering.beskrivning}
            </Box>
          )}
          <Box
            sx={{
              gap: 0.5,
              display: 'flex',
              alignItems: 'center',
              typography: 'caption',
              color: 'text.disabled',
            }}
          >
            {fToNow(notifiering.skapadVid)}
            <Box
              sx={{
                width: 2,
                height: 2,
                bgcolor: 'currentColor',
                borderRadius: '50%',
                opacity: 0.6,
              }}
            />
            {notifiering.domänEtikett}
            {/* Föreningen skrivs ut när den inte är den valda: listan spänner över alla
                användarens föreningar, och "Nytt meddelande i din brevlåda" säger inget om
                vilket hem det gäller för den som är med i flera. */}
            {notifiering.orgNamn && notifiering.orgId !== valdOrgId && (
              <>
                <Box
                  sx={{
                    width: 2,
                    height: 2,
                    bgcolor: 'currentColor',
                    borderRadius: '50%',
                    opacity: 0.6,
                  }}
                />
                {notifiering.orgNamn}
              </>
            )}
          </Box>
        </>
      }
    />
  );

  return (
    <ListItemButton
      onClick={() => onKlick(notifiering)}
      sx={{
        py: 1.5,
        px: 2.5,
        gap: 2,
        alignItems: 'flex-start',
        borderBottom: (theme) => `dashed 1px ${theme.vars.palette.divider}`,
      }}
    >
      {!notifiering.läst && (
        <Box
          sx={{
            top: 26,
            width: 8,
            height: 8,
            right: 20,
            borderRadius: '50%',
            bgcolor: 'info.main',
            position: 'absolute',
          }}
        />
      )}

      {renderIkon()}
      {renderText()}

      {!notifiering.arkiverad && (
        <Tooltip title="Arkivera">
          <IconButton
            size="small"
            aria-label={`Arkivera: ${notifiering.titel}`}
            onClick={(event) => {
              // Utan detta hade klicket också räknats som "öppna notifieringen" och navigerat bort.
              event.stopPropagation();
              onArkivera(notifiering.id);
            }}
            sx={{ mt: 2.5, color: 'text.disabled' }}
          >
            <Iconify width={16} icon="solar:archive-minimalistic-bold" />
          </IconButton>
        </Tooltip>
      )}
    </ListItemButton>
  );
}
